// PreviewBarV2 — UI padrão global. Barra de modo prévia compacta + diálogo
// de confirmação antes de criar um agendamento real durante o teste.
// Novo arquivo, não altera nada do fluxo existente — só é usado quando
// isPreview && pilotUI, dentro de BookingPage.tsx.
import React, { useEffect, useState } from 'react';
import { Box, Typography, Dialog, DialogContent, DialogActions, Button } from '@mui/material';
import { ArrowBackIosNew, WarningAmberRounded } from '@mui/icons-material';

// Navega direto para /dashboard na MESMA janela — não depende de history.back(),
// que falha quando o PWA foi aberto direto nessa URL (sem histórico anterior).
function goToDashboard() {
  window.location.href = window.location.origin + '/dashboard';
}

export function PreviewBarV2({ hasSelection }: { hasSelection: boolean }) {
  // Animação curta só na entrada (nada de pulsar continuamente)
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 20);
    return () => clearTimeout(t);
  }, []);

  return (
    <Box
      role="button"
      aria-label="Voltar ao painel"
      onClick={goToDashboard}
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2100, // acima do bottom-sheet de serviços selecionados (zIndex 300)
        height: 44,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        bgcolor: 'rgba(15,23,42,0.96)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        transform: entered ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 0.25s ease-out',
        '&:active': { bgcolor: 'rgba(30,41,59,0.98)' },
      }}
    >
      <ArrowBackIosNew sx={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', flexShrink: 0 }} />
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
        Voltar ao painel
      </Typography>
      {/* divisor fino — em sx do MUI, width:1 = 100%; usar '1px' de propósito */}
      <Box sx={{ width: '1px', height: 12, bgcolor: 'rgba(255,255,255,0.25)', mx: 0.5, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', flexShrink: 0 }}>
        Modo de prévia
      </Typography>
    </Box>
  );
}

// Quanto a barra "ocupa" no fundo da tela — usado por quem posiciona outros
// elementos fixos (ex.: o resumo de serviços selecionados) para não sobrepor.
export const PREVIEW_BAR_HEIGHT = 'calc(44px + env(safe-area-inset-bottom, 0px))';

export function TestBookingConfirmDialog({
  open, onCancel, onConfirm, loading,
}: { open: boolean; onCancel: () => void; onConfirm: () => void; loading?: boolean }) {
  return (
    <Dialog open={open} onClose={() => { if (!loading) onCancel(); }} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3, m: 2 } } }}>
      <DialogContent sx={{ pt: 3.5, textAlign: 'center' }}>
        <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: '#fff7ed', display: 'flex',
          alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <WarningAmberRounded sx={{ fontSize: 28, color: '#d97706' }} />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#1a1a1a', mb: 1 }}>
          Você está testando como cliente
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: '#666', lineHeight: 1.5 }}>
          Se continuar, será criado um agendamento real — não é uma simulação.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1, flexDirection: 'column', gap: 1 }}>
        <Button fullWidth variant="contained" disabled={loading} onClick={onConfirm}
          sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2.5, py: 1.2,
            bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
          Criar agendamento de teste
        </Button>
        <Button fullWidth variant="outlined" disabled={loading} onClick={onCancel}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2.5, py: 1.2,
            color: '#555', borderColor: '#e0e0e0' }}>
          Voltar e revisar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
